import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import EditorHeader from "../../components/editor/EditorHeader"
import EditorSidebar from "../../components/editor/EditorSidebar"
import EditorCanvas from "../../components/editor/EditorCanvas"
import InspectorPanel from "../../components/editor/InspectorPanel"
import CourseEditForm from "../../components/editor/CourseEditForm"
import CourseCreationForm from "../../components/editor/CourseCreationForm"
import CoursePreviewModal from "../../components/editor/CoursePreviewModal"
import CoursePreviewContent from "../../components/editor/CoursePreviewContent"
import CourseImageForm from "../../components/editor/CourseImageForm"
import CourseConfigurationForm from "../../components/editor/CourseConfigurationForm"
import ModuleForm from "../../components/editor/ModuleForm"
import LessonForm from "../../components/editor/LessonForm"
import BlockForm from "../../components/editor/BlockForm"
import QuizForm from "../../components/editor/QuizForm"
import EditorFooter from "../../components/editor/EditorFooter"
import TranslationManager from "../../components/editor/TranslationManager"
import { ModulePreview, BlockPreview, QuizPreview, LessonPreview } from "../../components/editor/EntityPreview"
import { useGet } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { useCourseCreation } from "../../hooks/useCourseCreation"
import { useCourseManagement } from "../../hooks/useCourseManagement"
import { api } from "../../utils/apiComponent/apiClient"
import { showSuccessToast, showErrorToast } from "../../utils/toast"
import { logger } from "../../utils/logger"
import { Loader2 } from "lucide-react"

// Système d'historique pour undo/redo
class HistoryManager {
  constructor() {
    this.history = []
    this.currentIndex = -1
    this.maxHistorySize = 50
  }

  push(state) {
    // Supprimer les états futurs si on est au milieu de l'historique
    this.history = this.history.slice(0, this.currentIndex + 1)
    
    // Ajouter le nouvel état
    this.history.push(JSON.parse(JSON.stringify(state))) // Deep clone
    
    // Limiter la taille de l'historique
    if (this.history.length > this.maxHistorySize) {
      this.history.shift()
    } else {
      this.currentIndex++
    }
  }

  undo() {
    if (this.canUndo()) {
      this.currentIndex--
      return JSON.parse(JSON.stringify(this.history[this.currentIndex]))
    }
    return null
  }

  redo() {
    if (this.canRedo()) {
      this.currentIndex++
      return JSON.parse(JSON.stringify(this.history[this.currentIndex]))
    }
    return null
  }

  canUndo() {
    return this.currentIndex > 0
  }

  canRedo() {
    return this.currentIndex < this.history.length - 1
  }

  getCurrent() {
    if (this.currentIndex >= 0 && this.currentIndex < this.history.length) {
      return JSON.parse(JSON.stringify(this.history[this.currentIndex]))
    }
    return null
  }

  clear() {
    this.history = []
    this.currentIndex = -1
  }
}

export default function AdvancedCourseEditorPage() {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  
  // Get courseId from params - handle both /new/editor and /:id/editor routes
  const courseId = params.id || (location.pathname.includes('/new/editor') ? 'new' : undefined)
  
  const { createCourse, createModule, createLesson, createBlock, createQuiz } = useCourseCreation()
  const { updateCourse, publishCourse, unpublishCourse, updateModule, moveModuleUp, moveModuleDown, deleteModule, updateLesson, deleteLesson, updateBlock, deleteBlock, reorderLessonBlocks, deleteCourse } = useCourseManagement()

  // Check if creating new course
  const isCreatingNew = courseId === 'new' || !courseId || courseId === 'undefined'
  
  // State for new course creation
  const [newCourseData, setNewCourseData] = useState({
    title: 'Nouveau cours',
    description: '',
    price: 0,
    categoryId: '',
    level: 'BEGINNER',
    language: 'Français',
    currency: 'XOF',
    status: 'DRAFT',
    isFree: false,
    isFeatured: false,
    isPublished: false,
    maxStudents: 100,
    certificate: false,
    accessDurationDays: 90,
    duration: 0,
    originalPrice: 0
  })

  // Fetch course data (only if not creating new and courseId is valid)
  // Only call API if we have a valid courseId
  const shouldFetchCourse = !isCreatingNew && courseId && courseId !== 'undefined' && courseId !== undefined
  // Only use the endpoint if we should fetch the course, otherwise pass null to avoid unnecessary API calls
  const courseEndpoint = shouldFetchCourse && courseId 
    ? `${endpoints.getCourse(courseId)}` 
    : null
  const courseQuery = useGet(courseEndpoint)
  const course = shouldFetchCourse ? courseQuery.data : null
  const courseLoading = shouldFetchCourse ? courseQuery.loading : false
  const courseError = shouldFetchCourse ? courseQuery.error : null
  const refetchCourse = shouldFetchCourse ? courseQuery.execute : () => {}

  // Use course data or new course data
  const currentCourse = isCreatingNew ? newCourseData : course

  // État local pour les modules (pour permettre les mises à jour locales)
  const [localModules, setLocalModules] = useState(null)

  // Synchroniser les modules locaux avec les données du cours
  useEffect(() => {
    if (course) {
      const courseModules = course.modules || course.contentModules || []
      setLocalModules(courseModules)
    } else if (isCreatingNew) {
      setLocalModules([])
    }
  }, [course, isCreatingNew])

  // Get modules from course data (course already contains modules)
  // Utiliser les modules locaux s'ils existent, sinon utiliser ceux du cours
  const modules = useMemo(() => {
    if (isCreatingNew) {
      return []
    }
    // Utiliser les modules locaux s'ils sont définis, sinon utiliser ceux du cours
    if (localModules !== null) {
      return localModules
    }
    if (!course) {
      return []
    }
    // Les modules peuvent être dans course.modules ou course.contentModules selon l'API
    return course.modules || course.contentModules || []
  }, [course, isCreatingNew, localModules])
  
  const modulesLoading = courseLoading
  const refetchModules = refetchCourse

  // State
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [selectedModuleId, setSelectedModuleId] = useState(null)
  const [selectedLessonId, setSelectedLessonId] = useState(null) // NEW: Lesson selection
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [selectedQuizId, setSelectedQuizId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState('idle') // idle, saving, saved, error
  const [lastSaved, setLastSaved] = useState(null)
  const [showInspector, setShowInspector] = useState(true) // Toujours visible par défaut
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [showTranslations, setShowTranslations] = useState(false)
  const [translationType, setTranslationType] = useState(null) // 'course', 'module', 'lesson', 'block', 'quiz'
  const [translationEntityId, setTranslationEntityId] = useState(null)
  const [translationEntityData, setTranslationEntityData] = useState(null)
  const [translationInitialLanguage, setTranslationInitialLanguage] = useState(null) // Langue à ouvrir directement
  const [courseCreated, setCourseCreated] = useState(!isCreatingNew) // Track if course is created
  const [notifications, setNotifications] = useState([])
  const [tips, setTips] = useState([])
  const [isCreatingModule, setIsCreatingModule] = useState(false)
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(false)
  const [isLoadingLesson, setIsLoadingLesson] = useState(false) // NEW: Loading state for lesson
  const [isLoadingBlock, setIsLoadingBlock] = useState(false) // NEW: Loading state for block
  const [isLoadingCourseConfig, setIsLoadingCourseConfig] = useState(false) // NEW: Loading state for course configuration
  const [courseConfigData, setCourseConfigData] = useState(null) // NEW: Course data loaded for configuration
  const [isReorderingBlocks, setIsReorderingBlocks] = useState(false) // Loading state for block reordering
  // Canvas view states
  const [canvasView, setCanvasView] = useState(null) // null, 'create-module', 'edit-module', 'create-lesson', 'edit-lesson', 'create-block', 'edit-block', 'create-quiz', 'edit-quiz'
  const [editingModule, setEditingModule] = useState(null)
  const [editingLesson, setEditingLesson] = useState(null) // NEW: Editing lesson
  const [editingBlock, setEditingBlock] = useState(null)
  const [editingQuiz, setEditingQuiz] = useState(null)
  const [creatingBlockType, setCreatingBlockType] = useState(null)
  
  // History management
  const historyManager = useRef(new HistoryManager())
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  // Initialize selected course by default on load
  useEffect(() => {
    if (!isCreatingNew && currentCourse && currentCourse.id && !courseLoading) {
      // Si aucun élément n'est sélectionné, sélectionner le cours par défaut
      if (!selectedModuleId && !selectedLessonId && !selectedBlockId && !selectedQuizId && !selectedCourseId) {
        setSelectedCourseId(currentCourse.id)
      }
    }
  }, [isCreatingNew, currentCourse, courseLoading, selectedModuleId, selectedLessonId, selectedBlockId, selectedQuizId, selectedCourseId])

  // Get lessons from selected module
  const currentLessons = useMemo(() => {
    if (!selectedModuleId || !modules || modules.length === 0) {
      return []
    }
    const selectedModule = modules.find(m => m.id === selectedModuleId)
    if (!selectedModule) {
      return []
    }
    // Les leçons sont dans module.lessons selon la nouvelle structure
    const lessons = selectedModule.lessons || []
    // Trier les leçons par ordre
    return [...lessons].sort((a, b) => (a.order || 0) - (b.order || 0))
  }, [selectedModuleId, modules])

  // Get blocks from selected lesson (NEW: blocks are now in lessons, not modules)
  const currentBlocks = useMemo(() => {
    if (!selectedLessonId || !currentLessons || currentLessons.length === 0) {
      return []
    }
    const selectedLesson = currentLessons.find(l => l.id === selectedLessonId)
    if (!selectedLesson) {
      return []
    }
    // Les blocs sont dans lesson.blocks selon la nouvelle structure
    const blocks = selectedLesson.blocks || []
    // Trier les blocs par ordre
    return [...blocks].sort((a, b) => (a.order || 0) - (b.order || 0))
  }, [selectedLessonId, currentLessons])

  const blocksLoading = false // Pas besoin de loading séparé car les blocs viennent avec les modules

  // Get selected block
  const selectedBlock = currentBlocks?.find(b => b.id === selectedBlockId)

  // Helper function to get the currently selected entity from hierarchy
  const getSelectedEntityFromHierarchy = useCallback(() => {
    // Priority: Quiz > Block > Lesson > Module > Course
    if (selectedQuizId) {
      // Find quiz in modules
      for (const module of modules) {
        if (module.quiz) {
          const moduleQuiz = module.quiz.quiz || module.quiz
          if (moduleQuiz.id === selectedQuizId) {
            return {
              type: 'quiz',
              id: moduleQuiz.id,
              data: editingQuiz || moduleQuiz
            }
          }
        }
      }
      // If quiz is in editingQuiz but not found in modules, use editingQuiz
      if (editingQuiz) {
        return {
          type: 'quiz',
          id: editingQuiz.id,
          data: editingQuiz
        }
      }
    }
    
    if (selectedBlockId) {
      const block = currentBlocks?.find(b => b.id === selectedBlockId) || editingBlock
      if (block) {
        return {
          type: 'block',
          id: block.id,
          data: block
        }
      }
    }
    
    if (selectedLessonId) {
      const lesson = currentLessons?.find(l => l.id === selectedLessonId) || editingLesson
      if (lesson) {
        return {
          type: 'lesson',
          id: lesson.id,
          data: lesson
        }
      }
    }
    
    if (selectedModuleId) {
      const module = modules.find(m => m.id === selectedModuleId) || editingModule
      if (module) {
        return {
          type: 'module',
          id: module.id,
          data: module
        }
      }
    }
    
    // Default to course if nothing else is selected
    if (!isCreatingNew && currentCourse) {
      return {
        type: 'course',
        id: courseId,
        data: currentCourse
      }
    }
    
    return null
  }, [selectedQuizId, selectedBlockId, selectedLessonId, selectedModuleId, modules, currentBlocks, currentLessons, editingQuiz, editingBlock, editingLesson, editingModule, isCreatingNew, currentCourse, courseId])

  // Mettre à jour automatiquement le panneau de traduction quand la sélection change
  useEffect(() => {
    if (showTranslations) {
      const selectedEntity = getSelectedEntityFromHierarchy()
      if (selectedEntity) {
        // Mettre à jour seulement si l'entité a changé
        if (translationEntityId !== selectedEntity.id || translationType !== selectedEntity.type) {
          setTranslationType(selectedEntity.type)
          setTranslationEntityId(selectedEntity.id)
          setTranslationEntityData(selectedEntity.data)
        }
      } else {
        // Si aucune entité n'est sélectionnée, fermer le panneau de traduction
        setShowTranslations(false)
        setTranslationType(null)
        setTranslationEntityId(null)
        setTranslationEntityData(null)
      }
    }
  }, [selectedQuizId, selectedBlockId, selectedLessonId, selectedModuleId, showTranslations, getSelectedEntityFromHierarchy, translationEntityId, translationType])

  // Save current state to history
  const saveToHistory = useCallback(() => {
    const state = {
      modules: modules,
      selectedModuleId,
      selectedLessonId,
      selectedBlockId,
      timestamp: Date.now()
    }
    historyManager.current.push(state)
    setCanUndo(historyManager.current.canUndo())
    setCanRedo(historyManager.current.canRedo())
  }, [modules, selectedModuleId, selectedLessonId, selectedBlockId])

  // Initialize history
  useEffect(() => {
    if (modules && modules.length > 0) {
      saveToHistory()
    }
  }, []) // Only on mount

  // Handle undo
  const handleUndo = useCallback(() => {
    const previousState = historyManager.current.undo()
    if (previousState) {
      // Restore state (simplified - in production, you'd need to restore from API)
      setSelectedModuleId(previousState.selectedModuleId)
      setSelectedBlockId(previousState.selectedBlockId)
      setCanUndo(historyManager.current.canUndo())
      setCanRedo(historyManager.current.canRedo())
      addNotification('Action annulée', 'success')
    }
  }, [])

  // Handle redo
  const handleRedo = useCallback(() => {
    const nextState = historyManager.current.redo()
    if (nextState) {
      setSelectedModuleId(nextState.selectedModuleId)
      setSelectedBlockId(nextState.selectedBlockId)
      setCanUndo(historyManager.current.canUndo())
      setCanRedo(historyManager.current.canRedo())
      addNotification('Action rétablie', 'success')
    }
  }, [])

  // Add notification
  const addNotification = useCallback((message, type = 'info') => {
    const id = Date.now()
    setNotifications(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id))
    }, 5000)
  }, [])

  // Add tip
  const addTip = useCallback((message) => {
    setTips(prev => [...prev, message])
  }, [])

  // Remove tip
  const removeTip = useCallback((index) => {
    setTips(prev => prev.filter((_, i) => i !== index))
  }, [])

  // Create new course from modal
  const handleCreateCourseFromModal = useCallback(async (courseData) => {
    if (isSaving) return
    
    setIsSaving(true)
    setSaveStatus('saving')
    
    try {
      const createdCourse = await createCourse(courseData)
      
      if (createdCourse && createdCourse.id) {
        // Update state
        setNewCourseData(courseData)
        setCourseCreated(true)
        
        // Redirect to editor with new course ID
        navigate(`/admin/courses/${createdCourse.id}/editor`, { replace: true })
        showSuccessToast({ message: "Cours créé avec succès !" })
        addNotification('Cours créé avec succès', 'success')
        setSaveStatus('saved')
      }
    } catch (err) {
      logger.error('Create course error', err)
      setSaveStatus('error')
      showErrorToast({ message: err?.message || "Erreur lors de la création du cours" })
      addNotification('Erreur lors de la création du cours', 'error')
    } finally {
      setIsSaving(false)
    }
  }, [isSaving, createCourse, navigate, addNotification])

  // Create new course (legacy - for save button)
  const handleCreateCourse = useCallback(async () => {
    if (!isCreatingNew || isSaving) return
    
    setIsSaving(true)
    setSaveStatus('saving')
    
    try {
      const createdCourse = await createCourse(newCourseData)
      
      if (createdCourse && createdCourse.id) {
        // Redirect to editor with new course ID
        navigate(`/admin/courses/${createdCourse.id}/editor`, { replace: true })
        showSuccessToast({ message: "Cours créé avec succès !" })
        addNotification('Cours créé avec succès', 'success')
      }
    } catch (err) {
      logger.error('Create course error', err)
      setSaveStatus('error')
      showErrorToast({ message: err?.message || "Erreur lors de la création du cours" })
      addNotification('Erreur lors de la création du cours', 'error')
    } finally {
      setIsSaving(false)
    }
  }, [isCreatingNew, newCourseData, isSaving, createCourse, navigate, addNotification])

  // Auto-save
  const autoSave = useCallback(async () => {
    if (isCreatingNew) {
      // For new course, create it first
      await handleCreateCourse()
      return
    }
    
    if (!course || isSaving) return
    
    setIsSaving(true)
    setSaveStatus('saving')
    
    try {
      // Save course changes if any
      // In a real implementation, you'd track what changed
      await api.patch(endpoints.updateCourse(courseId), {
        title: course.title,
        // ... other fields
      })
      
      setSaveStatus('saved')
      setLastSaved(new Date())
      addNotification('Sauvegarde automatique réussie', 'success')
      
      setTimeout(() => {
        setSaveStatus('idle')
      }, 2000)
    } catch (err) {
      logger.error('Auto-save error', err)
      setSaveStatus('error')
      addNotification('Erreur lors de la sauvegarde automatique', 'error')
    } finally {
      setIsSaving(false)
    }
  }, [isCreatingNew, course, courseId, isSaving, handleCreateCourse, addNotification])

  // Manual save
  const handleSave = useCallback(async () => {
    if (isCreatingNew) {
      await handleCreateCourse()
    } else {
      await autoSave()
      saveToHistory()
    }
  }, [isCreatingNew, handleCreateCourse, autoSave, saveToHistory])

  // Publish course (deprecated - use handleTogglePublished instead)
  const _handlePublish = useCallback(async () => {
    if (isCreatingNew) {
      // Create course first, then publish
      try {
        const createdCourse = await createCourse({ ...newCourseData, isPublished: true, status: 'PUBLISHED' })
        if (createdCourse && createdCourse.id) {
          navigate(`/admin/courses/${createdCourse.id}/editor`, { replace: true })
          showSuccessToast({ message: "Cours créé et publié avec succès !" })
          addNotification('Cours créé et publié', 'success')
        }
      } catch (err) {
        logger.error('Publish error', err)
        showErrorToast({ message: err?.message || "Erreur lors de la publication" })
        addNotification('Erreur lors de la publication', 'error')
      }
    } else {
      if (!courseId || courseId === 'undefined') {
        addNotification('ID du cours invalide', 'error')
        return
      }
      try {
        await api.patch(endpoints.publishCourse(courseId))
        showSuccessToast({ message: "Cours publié avec succès !" })
        refetchCourse()
        addNotification('Cours publié', 'success')
      } catch (err) {
        logger.error('Publish error', err)
        showErrorToast({ message: err?.message || "Erreur lors de la publication" })
        addNotification('Erreur lors de la publication', 'error')
      }
    }
  }, [isCreatingNew, newCourseData, courseId, createCourse, navigate, refetchCourse, addNotification])

  // Preview course
  const handlePreview = useCallback(() => {
    if (isCreatingNew) {
      addNotification('Veuillez d\'abord sauvegarder le cours', 'info')
      return
    }
    setShowPreviewModal(true)
  }, [isCreatingNew, addNotification])

  // Handle module selection
  const handleModuleSelect = useCallback((moduleId) => {
    setSelectedModuleId(moduleId)
    setSelectedLessonId(null) // NEW: Clear lesson selection when module changes
    setSelectedBlockId(null)
    setSelectedQuizId(null)
    // Just select the module, don't open edit form - preview will be shown
    setCanvasView(null)
    setShowTranslations(false) // Fermer le panneau de traduction pour afficher la preview
    saveToHistory()
  }, [saveToHistory])

  // Handle lesson selection (NEW)
  const handleLessonSelect = useCallback(async (lessonId) => {
    setSelectedLessonId(lessonId)
    setSelectedBlockId(null)
    setSelectedQuizId(null)
    setIsLoadingLesson(true)
    setShowTranslations(false) // Fermer le panneau de traduction pour afficher la preview
    
    try {
      // Charger les détails complets de la leçon depuis l'API
      const response = await api.get(endpoints.getModuleLesson(lessonId))
      const lessonData = response?.data || response
      
      if (lessonData) {
        setEditingLesson(lessonData)
        // Don't open edit form - preview will be shown
        setCanvasView(null)
      } else {
        // Fallback: chercher la leçon dans les modules si l'API ne retourne pas de données
        let lesson = null
        for (const module of modules) {
          if (module.lessons && Array.isArray(module.lessons)) {
            const foundLesson = module.lessons.find(l => l.id === lessonId)
            if (foundLesson) {
              lesson = foundLesson
              break
            }
          }
        }
        if (lesson) {
          setEditingLesson(lesson)
          setCanvasView(null)
        } else {
          addNotification('Chapitre introuvable', 'error')
        }
      }
    } catch (err) {
      logger.error('Error loading lesson', err)
      // Fallback: chercher la leçon dans les modules en cas d'erreur
      let lesson = null
      for (const module of modules) {
        if (module.lessons && Array.isArray(module.lessons)) {
          const foundLesson = module.lessons.find(l => l.id === lessonId)
          if (foundLesson) {
            lesson = foundLesson
            break
          }
        }
      }
      if (lesson) {
        setEditingLesson(lesson)
        setCanvasView(null)
      } else {
        addNotification('Erreur lors du chargement du chapitre', 'error')
      }
    } finally {
      setIsLoadingLesson(false)
    }
    
    saveToHistory()
  }, [modules, saveToHistory, addNotification])

  // Handle block selection
  const handleBlockSelect = useCallback(async (blockId) => {
    setSelectedBlockId(blockId)
    setSelectedQuizId(null)
    setIsLoadingBlock(true)
    setShowTranslations(false) // Fermer le panneau de traduction pour afficher la preview
    
    try {
      // Charger les détails complets du bloc depuis l'API
      const response = await api.get(endpoints.getModuleBlock(blockId))
      const blockData = response?.data || response
      
      if (blockData) {
        setEditingBlock(blockData)
        // Don't open edit form - preview will be shown
        setCanvasView(null)
      } else {
        // Fallback: chercher le bloc dans les leçons si l'API ne retourne pas de données
        let block = null
        for (const module of modules) {
          if (module.lessons && Array.isArray(module.lessons)) {
            for (const lesson of module.lessons) {
              if (lesson.blocks && Array.isArray(lesson.blocks)) {
                const foundBlock = lesson.blocks.find(b => b.id === blockId)
                if (foundBlock) {
                  block = foundBlock
                  break
                }
              }
            }
            if (block) break
          }
        }
        if (block) {
          setEditingBlock(block)
          setCanvasView(null)
        } else {
          addNotification('Bloc introuvable', 'error')
        }
      }
    } catch (err) {
      logger.error('Error loading block', err)
      // Fallback: chercher le bloc dans les leçons en cas d'erreur
      let block = null
      for (const module of modules) {
        if (module.lessons && Array.isArray(module.lessons)) {
          for (const lesson of module.lessons) {
            if (lesson.blocks && Array.isArray(lesson.blocks)) {
              const foundBlock = lesson.blocks.find(b => b.id === blockId)
              if (foundBlock) {
                block = foundBlock
                break
              }
            }
          }
          if (block) break
        }
      }
      if (block) {
        setEditingBlock(block)
        setCanvasView(null)
      } else {
        addNotification('Erreur lors du chargement du bloc', 'error')
      }
    } finally {
      setIsLoadingBlock(false)
    }
    
    saveToHistory()
  }, [modules, saveToHistory, addNotification])

  // Handle course selection
  const handleCourseSelect = useCallback((courseId) => {
    setSelectedCourseId(courseId)
    setSelectedModuleId(null)
    setSelectedLessonId(null) // NEW: Clear lesson selection
    setSelectedBlockId(null)
    setSelectedQuizId(null)
    setCanvasView(null)
    setShowTranslations(false) // Fermer le panneau de traduction pour afficher la preview
    saveToHistory()
  }, [saveToHistory])

  // Fonction pour charger un quiz avec sa traduction
  const loadQuizWithTranslation = useCallback(async (quizId, languageCode = 'fr') => {
    setIsLoadingQuiz(true)
    try {
      // Charger le quiz de base
      const quizResponse = await api.get(endpoints.getQuizAdmin(quizId))
      const baseQuizData = quizResponse?.data || quizResponse
      
      if (!baseQuizData) {
        return null
      }
      
      // Si la langue n'est pas 'fr', charger la traduction
      if (languageCode && languageCode !== 'fr') {
        try {
          const translationResponse = await api.get(endpoints.getQuizTranslation(quizId, languageCode))
          const translation = translationResponse?.data || translationResponse
          
          if (translation) {
            // Fusionner les données de base avec la traduction
            const mergedQuiz = {
              ...baseQuizData,
              title: translation.title || baseQuizData.title,
              description: translation.description || baseQuizData.description,
              instructions: translation.instructions || baseQuizData.instructions
            }
            
            // Charger les traductions des questions si elles existent
            if (baseQuizData.questions && Array.isArray(baseQuizData.questions)) {
              const questionsWithTranslations = await Promise.all(
                baseQuizData.questions.map(async (question) => {
                  if (!question.id) return question
                  
                  try {
                    const questionTranslation = await api.get(
                      endpoints.getQuizQuestionTranslation(question.id, languageCode)
                    )
                    const qTranslation = questionTranslation?.data || questionTranslation
                    
                    if (qTranslation?.questionText) {
                      // Fusionner les données de la question avec sa traduction
                      const mergedQuestion = {
                        ...question,
                        question: qTranslation.questionText
                      }
                      
                      // Traduire les options si elles existent
                      if (question.options && Array.isArray(question.options) && qTranslation.options) {
                        mergedQuestion.options = question.options.map((option, index) => {
                          const translatedOption = qTranslation.options[index] || qTranslation.options.find(opt => opt.id === option.id)
                          return {
                            ...option,
                            text: translatedOption?.text || option.text
                          }
                        })
                      }
                      
                      return mergedQuestion
                    }
                  } catch (err) {
                    // Traduction de question non trouvée, utiliser la question de base
                    console.debug(`Question translation not found for ${question.id} in ${languageCode}`)
                  }
                  
                  return question
                })
              )
              mergedQuiz.questions = questionsWithTranslations
            }
            
            return mergedQuiz
          }
        } catch (translationError) {
          // Si la traduction n'existe pas, utiliser les données de base
          console.debug(`Quiz translation not found for ${quizId} in ${languageCode}`)
        }
      }
      
      return baseQuizData
    } catch (err) {
      logger.error('Error loading quiz with translation', err)
      return null
    } finally {
      setIsLoadingQuiz(false)
    }
  }, [])

  // Handle quiz selection
  const handleQuizSelect = useCallback(async (quizId) => {
    setSelectedQuizId(quizId)
    setSelectedBlockId(null)
    setShowTranslations(false) // Fermer le panneau de traduction pour afficher la preview
    
    const quizData = await loadQuizWithTranslation(quizId, 'fr')
    
    if (quizData) {
      setEditingQuiz(quizData)
      // Don't open edit form - preview will be shown
      setCanvasView(null)
    } else {
      // Fallback: chercher le quiz dans les modules si l'API ne retourne pas de données
      let quiz = null
      for (const module of modules) {
        if (module.quiz) {
          const moduleQuiz = module.quiz.quiz || module.quiz
          if (moduleQuiz.id === quizId) {
            quiz = moduleQuiz
            break
          }
        }
      }
      if (quiz) {
        setEditingQuiz(quiz)
        setCanvasView(null)
      } else {
        addNotification('Quiz introuvable', 'error')
      }
    }
    
    saveToHistory()
  }, [modules, saveToHistory, addNotification, loadQuizWithTranslation])

  // Handle module creation
  const handleCreateModule = useCallback(async (moduleData) => {
    if (isCreatingNew) {
      addNotification('Veuillez d\'abord sauvegarder le cours', 'info')
      return
    }
    setIsCreatingModule(true)
    try {
      await createModule({ ...moduleData, order: modules.length + 1 }, courseId)
      showSuccessToast({ message: "Module créé avec succès !" })
      refetchModules()
      saveToHistory()
      addNotification('Module créé', 'success')
      // Close the form and return to normal view
      setCanvasView(null)
      setEditingModule(null)
      // Recharger la page après la création
      window.location.reload()
    } catch (err) {
      logger.error('Error creating module', err)
      showErrorToast({ message: err?.message || "Erreur lors de la création du module" })
      addNotification('Erreur lors de la création du module', 'error')
    } finally {
      setIsCreatingModule(false)
    }
  }, [isCreatingNew, createModule, courseId, modules.length, refetchModules, saveToHistory, addNotification])

  // Handle module update
  const handleUpdateModule = useCallback(async (moduleData) => {
    if (!editingModule) return
    setIsCreatingModule(true)
    try {
      await updateModule(editingModule.id, moduleData)
      showSuccessToast({ message: "Module modifié avec succès !" })
      refetchModules()
      saveToHistory()
      addNotification('Module modifié', 'success')
      // Close the form and return to normal view
      setCanvasView(null)
      setEditingModule(null)
    } catch (err) {
      logger.error('Error updating module', err)
      showErrorToast({ message: err?.message || "Erreur lors de la modification du module" })
      addNotification('Erreur lors de la modification du module', 'error')
    } finally {
      setIsCreatingModule(false)
    }
  }, [editingModule, updateModule, refetchModules, saveToHistory, addNotification])

  // Handle lesson creation (NEW)
  const handleCreateLesson = useCallback(async (lessonData, moduleId) => {
    if (isCreatingNew) {
      addNotification('Veuillez d\'abord sauvegarder le cours', 'info')
      return
    }
    setIsCreatingModule(true) // Reuse loading state
    try {
      const targetModuleId = moduleId || selectedModuleId
      if (!targetModuleId) {
        throw new Error('Aucun module sélectionné')
      }
      const selectedModule = modules.find(m => m.id === targetModuleId)
      const lessonsCount = selectedModule?.lessons?.length || 0
      await createLesson({ ...lessonData, order: lessonsCount + 1 }, targetModuleId)
      showSuccessToast({ message: "Chapitre créée avec succès !" })
      refetchModules()
      saveToHistory()
      addNotification('Chapitre créée', 'success')
      // Close the form and return to normal view
      setCanvasView(null)
      setEditingLesson(null)
      // Recharger la page après la création
      window.location.reload()
    } catch (err) {
      logger.error('Error creating lesson', err)
      showErrorToast({ message: err?.message || "Erreur lors de la création du chapitre" })
      addNotification('Erreur lors de la création du chapitre', 'error')
    } finally {
      setIsCreatingModule(false)
    }
  }, [isCreatingNew, createLesson, selectedModuleId, modules, refetchModules, saveToHistory, addNotification])

  // Handle lesson update (NEW)
  const handleUpdateLesson = useCallback(async (lessonData) => {
    if (!editingLesson) return
    setIsCreatingModule(true) // Reuse loading state
    try {
      await updateLesson(editingLesson.id, lessonData)
      showSuccessToast({ message: "Chapitre modifiée avec succès !" })
      refetchModules()
      saveToHistory()
      addNotification('Chapitre modifiée', 'success')
      // Close the form and return to normal view
      setCanvasView(null)
      setEditingLesson(null)
    } catch (err) {
      logger.error('Error updating lesson', err)
      showErrorToast({ message: err?.message || "Erreur lors de la modification du chapitre" })
      addNotification('Erreur lors de la modification du chapitre', 'error')
    } finally {
      setIsCreatingModule(false)
    }
  }, [editingLesson, updateLesson, refetchModules, saveToHistory, addNotification])

  // Handle lesson delete (NEW)
  const handleLessonDelete = useCallback(async (lessonId) => {
    try {
      await deleteLesson(lessonId)
      refetchModules()
      saveToHistory()
      addNotification('Chapitre supprimé', 'success')
      // Si la leçon supprimée était sélectionnée, désélectionner
      if (selectedLessonId === lessonId) {
        setSelectedLessonId(null)
        setSelectedBlockId(null)
      }
    } catch (err) {
      logger.error('Error deleting lesson', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression'
      addNotification(errorMessage, 'error')
    }
  }, [deleteLesson, refetchModules, saveToHistory, addNotification, selectedLessonId])

  // Handle block creation - NOW USES lessonId instead of moduleId
  const handleCreateBlock = useCallback(async (blockData, lessonId) => {
    if (isCreatingNew) {
      addNotification('Veuillez d\'abord sauvegarder le cours', 'info')
      return
    }
    try {
      const targetLessonId = lessonId || selectedLessonId
      if (!targetLessonId) {
        addNotification('Veuillez sélectionner un chapitre d\'abord', 'info')
        return
      }
      await createBlock(blockData, targetLessonId)
      showSuccessToast({ message: "Bloc créé avec succès !" })
      refetchModules()
      saveToHistory()
      addNotification('Bloc créé', 'success')
      // Close the form and return to normal view
      setCanvasView(null)
      setCreatingBlockType(null)
      // Recharger la page après la création
      window.location.reload()
    } catch (err) {
      logger.error('Error creating block', err)
      showErrorToast({ message: err?.message || "Erreur lors de la création du bloc" })
      addNotification('Erreur lors de la création du bloc', 'error')
    }
  }, [isCreatingNew, createBlock, selectedLessonId, refetchModules, saveToHistory, addNotification])

  // Handle block update
  const handleUpdateBlock = useCallback(async (blockData) => {
    if (!editingBlock) return
    
    // LOG: Données reçues dans handleUpdateBlock
    console.log('[AdvancedCourseEditorPage] handleUpdateBlock - Données reçues:', {
      blockId: editingBlock.id,
      blockData: {
        ...blockData,
        image: blockData.image ? {
          name: blockData.image.name,
          type: blockData.image.type,
          size: blockData.image.size,
          isFile: blockData.image instanceof File
        } : undefined,
        imageFile: blockData.imageFile ? {
          name: blockData.imageFile.name,
          type: blockData.imageFile.type,
          size: blockData.imageFile.size,
          isFile: blockData.imageFile instanceof File
        } : undefined
      }
    })
    
    try {
      const result = await updateBlock(editingBlock.id, blockData)
      
      // LOG: Réponse reçue dans handleUpdateBlock
      console.log('[AdvancedCourseEditorPage] handleUpdateBlock - Réponse API:', result)
      
      showSuccessToast({ message: "Bloc modifié avec succès !" })
      
      // Rafraîchir les modules pour obtenir les données à jour
      await refetchModules()
      
      // Recharger le bloc mis à jour depuis l'API
      setIsLoadingBlock(true)
      try {
        const response = await api.get(endpoints.getModuleBlock(editingBlock.id))
        const updatedBlockData = response?.data || response
        if (updatedBlockData) {
          setEditingBlock(updatedBlockData)
        }
      } catch (err) {
        logger.error('Error reloading block after update', err)
        // Fallback: utiliser les données de la réponse de mise à jour si disponibles
        if (result) {
          setEditingBlock(result)
        }
      } finally {
        setIsLoadingBlock(false)
      }
      
      saveToHistory()
      addNotification('Bloc modifié', 'success')
      // Close the form and return to normal view
      setCanvasView(null)
    } catch (err) {
      // LOG: Erreur dans handleUpdateBlock
      console.error('[AdvancedCourseEditorPage] handleUpdateBlock - Erreur:', err)
      logger.error('Error updating block', err)
      setIsLoadingBlock(false) // S'assurer que le loading est désactivé en cas d'erreur
      showErrorToast({ message: err?.message || "Erreur lors de la modification du bloc" })
      addNotification('Erreur lors de la modification du bloc', 'error')
    }
  }, [editingBlock, updateBlock, refetchModules, saveToHistory, addNotification, api])

  // Handle quiz creation
  const handleCreateQuiz = useCallback(async (quizData, moduleId) => {
    if (isCreatingNew) {
      addNotification('Veuillez d\'abord sauvegarder le cours', 'info')
      return null
    }
    
    // Valider qu'un module est sélectionné
    const targetModuleId = moduleId || selectedModuleId
    if (!targetModuleId) {
      const errorMsg = "Veuillez sélectionner un module avant de créer un quiz"
      showErrorToast({ message: errorMsg })
      addNotification(errorMsg, 'error')
      throw new Error(errorMsg)
    }
    
    try {
      console.log('handleCreateQuiz appelé avec:', quizData, 'moduleId:', targetModuleId)
      const createdQuiz = await createQuiz(quizData, targetModuleId)
      console.log('Quiz créé retourné par createQuiz:', createdQuiz)
      showSuccessToast({ message: "Quiz créé avec succès !" })
      refetchModules()
      saveToHistory()
      addNotification('Quiz créé', 'success')
      // Retourner le quiz créé pour que QuizForm puisse créer les questions
      console.log('handleCreateQuiz retourne:', createdQuiz)
      // Recharger la page après la création
      window.location.reload()
      return createdQuiz
    } catch (err) {
      logger.error('Error creating quiz', err)
      showErrorToast({ message: err?.message || "Erreur lors de la création du quiz" })
      addNotification('Erreur lors de la création du quiz', 'error')
      throw err
    }
  }, [isCreatingNew, createQuiz, selectedModuleId, refetchModules, saveToHistory, addNotification])

  // Handle quiz update
  const handleUpdateQuiz = useCallback(async (quizData) => {
    if (!editingQuiz) return
    try {
      const updatedQuiz = await api.patch(endpoints.updateQuiz(editingQuiz.id), quizData)
      showSuccessToast({ message: "Quiz modifié avec succès !" })
      // Ne pas recharger les modules immédiatement - attendre que les questions soient créées
      // refetchModules() sera appelé après la création des questions dans QuizForm
      saveToHistory()
      addNotification('Quiz modifié', 'success')
      // Ne pas fermer le formulaire immédiatement - laisser QuizForm gérer la création des questions
      // setCanvasView(null)
      // setEditingQuiz(null)
      // Return the updated quiz so QuizForm can use it
      return updatedQuiz
    } catch (err) {
      logger.error('Error updating quiz', err)
      showErrorToast({ message: err?.message || "Erreur lors de la modification du quiz" })
      addNotification('Erreur lors de la modification du quiz', 'error')
      throw err
    }
  }, [editingQuiz, saveToHistory, addNotification])

  // Handle module reorder
  const handleModuleReorder = useCallback(async (newOrder) => {
    try {
      // Update order for each module
      const promises = newOrder.map((moduleId, index) => {
        const module = modules.find(m => m.id === moduleId)
        if (module && module.order !== index + 1) {
          return updateModule(moduleId, { order: index + 1 })
        }
        return Promise.resolve()
      })
      await Promise.all(promises)
      refetchModules()
      saveToHistory()
      addNotification('Ordre des modules mis à jour', 'success')
    } catch (err) {
      logger.error('Error reordering modules', err)
      addNotification('Erreur lors du réordonnancement', 'error')
    }
  }, [modules, updateModule, refetchModules, saveToHistory, addNotification])

  // Handle module move up
  const handleModuleMoveUp = useCallback(async (moduleId, currentIndex) => {
    if (currentIndex === 0) return
    try {
      await moveModuleUp(moduleId)
      refetchModules()
      saveToHistory()
      addNotification('Module déplacé vers le haut', 'success')
    } catch (err) {
      logger.error('Error moving module up', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du déplacement'
      addNotification(errorMessage, 'error')
    }
  }, [moveModuleUp, refetchModules, saveToHistory, addNotification])

  // Handle module move down
  const handleModuleMoveDown = useCallback(async (moduleId, currentIndex) => {
    if (currentIndex >= modules.length - 1) return
    try {
      await moveModuleDown(moduleId)
      refetchModules()
      saveToHistory()
      addNotification('Module déplacé vers le bas', 'success')
    } catch (err) {
      logger.error('Error moving module down', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du déplacement'
      addNotification(errorMessage, 'error')
    }
  }, [moveModuleDown, modules.length, refetchModules, saveToHistory, addNotification])

  // Handle module delete
  const handleModuleDelete = useCallback(async (moduleId) => {
    try {
      await deleteModule(moduleId)
      refetchModules()
      saveToHistory()
      addNotification('Module supprimé', 'success')
      // Si le module supprimé était sélectionné, désélectionner
      if (selectedModuleId === moduleId) {
        setSelectedModuleId(null)
        setSelectedBlockId(null)
      }
    } catch (err) {
      logger.error('Error deleting module', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression'
      addNotification(errorMessage, 'error')
    }
  }, [deleteModule, refetchModules, saveToHistory, addNotification, selectedModuleId])

  // Handle block delete
  const handleBlockDelete = useCallback(async (blockId) => {
    try {
      await deleteBlock(blockId)
      refetchModules()
      saveToHistory()
      addNotification('Bloc supprimé', 'success')
      // Si le bloc supprimé était sélectionné, désélectionner
      if (selectedBlockId === blockId) {
        setSelectedBlockId(null)
      }
    } catch (err) {
      logger.error('Error deleting block', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression'
      addNotification(errorMessage, 'error')
    }
  }, [deleteBlock, refetchModules, saveToHistory, addNotification, selectedBlockId])

  // Handle quiz delete
  const handleQuizDelete = useCallback(async (quizId) => {
    try {
      // Find the module that contains this quiz
      const moduleWithQuiz = modules.find(m => {
        if (m.quiz) {
          const moduleQuiz = m.quiz.quiz || m.quiz
          return moduleQuiz.id === quizId
        }
        return false
      })
      
      if (!moduleWithQuiz) {
        addNotification('Quiz introuvable', 'error')
        return
      }

      // Delete quiz using API
      await api.delete(endpoints.deleteQuiz(quizId))
      refetchModules()
      saveToHistory()
      addNotification('Quiz supprimé', 'success')
      // Si le quiz supprimé était sélectionné, désélectionner
      if (selectedQuizId === quizId) {
        setSelectedQuizId(null)
        setCanvasView(null)
        setEditingQuiz(null)
      }
    } catch (err) {
      logger.error('Error deleting quiz', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression'
      addNotification(errorMessage, 'error')
    }
  }, [modules, refetchModules, saveToHistory, addNotification, selectedQuizId])

  // Handle course status change (deprecated - kept for backward compatibility)
  const handleCourseStatusChange = useCallback(async (newStatus) => {
    if (!courseId || isCreatingNew) return
    
    try {
      if (newStatus === 'PUBLISHED') {
        await publishCourse(courseId)
        addNotification('Cours publié', 'success')
      } else if (newStatus === 'DRAFT') {
        await unpublishCourse(courseId)
        addNotification('Cours dépublié', 'success')
      } else {
        // Pour ARCHIVED, on utilise updateCourse
        await updateCourse(courseId, { status: newStatus })
        addNotification('Statut du cours mis à jour', 'success')
      }
      refetchCourse()
      saveToHistory()
    } catch (err) {
      logger.error('Error changing course status', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du changement de statut'
      addNotification(errorMessage, 'error')
    }
  }, [courseId, isCreatingNew, publishCourse, unpublishCourse, updateCourse, refetchCourse, saveToHistory, addNotification])

  // État de chargement pour la publication
  const [isPublishing, setIsPublishing] = useState(false)

  // Handle course published toggle
  const handleTogglePublished = useCallback(async () => {
    if (!courseId || isCreatingNew || !currentCourse || isPublishing) return
    
    setIsPublishing(true)
    try {
      if (currentCourse.isPublished) {
        await unpublishCourse(courseId)
        addNotification('Cours dépublié', 'success')
      } else {
        await publishCourse(courseId)
        addNotification('Cours publié', 'success')
      }
      refetchCourse()
      saveToHistory()
      // Recharger la page après succès
      window.location.reload()
    } catch (err) {
      logger.error('Error toggling course published status', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du changement de statut'
      addNotification(errorMessage, 'error')
      setIsPublishing(false)
    }
  }, [courseId, isCreatingNew, currentCourse, isPublishing, publishCourse, unpublishCourse, refetchCourse, saveToHistory, addNotification])

  // Handle course delete
  const handleCourseDelete = useCallback(async () => {
    if (!courseId || isCreatingNew) return
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le cours "${currentCourse?.title || 'ce cours'}" ? Cette action est irréversible et supprimera tous les modules et blocs associés.`)) {
      try {
        await deleteCourse(courseId)
        addNotification('Cours supprimé', 'success')
        navigate('/admin/courses')
      } catch (err) {
        logger.error('Error deleting course', err)
        const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression du cours'
        addNotification(errorMessage, 'error')
      }
    }
  }, [courseId, isCreatingNew, currentCourse, deleteCourse, addNotification, navigate])

  // Handle block reorder
  const handleBlockReorder = useCallback(async (lessonId, newOrder) => {
    if (!lessonId) {
      addNotification('Aucune leçon sélectionnée', 'error')
      return
    }
    
    // newOrder est un tableau de blockIds (strings)
    if (!Array.isArray(newOrder) || newOrder.length === 0) {
      addNotification('Ordre invalide', 'error')
      return
    }
    
    setIsReorderingBlocks(true)
    try {
      // Attendre la réponse du backend avant de mettre à jour localement
      await reorderLessonBlocks(lessonId, newOrder)
      
      // Mise à jour locale après confirmation du backend
      setLocalModules(prevModules => {
        if (!prevModules) return prevModules
        
        return prevModules.map(module => {
          // Vérifier si ce module contient la leçon
          const lessonIndex = module.lessons?.findIndex(l => l.id === lessonId)
          if (lessonIndex === -1 || lessonIndex === undefined) {
            return module
          }
          
          // Créer une copie du module avec les leçons mises à jour
          const updatedLessons = [...(module.lessons || [])]
          const lesson = updatedLessons[lessonIndex]
          
          if (!lesson || !lesson.blocks || lesson.blocks.length === 0) {
            return module
          }
          
          // Créer un map pour accéder rapidement aux blocs par ID
          const blocksMap = new Map(lesson.blocks.map(block => [block.id, block]))
          
          // Réordonner les blocs selon le nouvel ordre (newOrder contient tous les IDs dans le nouvel ordre)
          const updatedBlocks = newOrder
            .map(blockId => blocksMap.get(blockId))
            .filter(Boolean) // Filtrer les undefined au cas où un ID n'existe pas
          
          // Mettre à jour l'ordre (order) de chaque bloc selon sa position dans newOrder
          const blocksWithOrder = updatedBlocks.map((block, index) => ({
            ...block,
            order: index + 1
          }))
          
          // Mettre à jour la leçon avec les nouveaux blocs
          updatedLessons[lessonIndex] = {
            ...lesson,
            blocks: blocksWithOrder
          }
          
          return {
            ...module,
            lessons: updatedLessons
          }
        })
      })
      
      saveToHistory()
      addNotification('Ordre des blocs mis à jour', 'success')
    } catch (err) {
      logger.error('Error reordering blocks', err)
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors du réordonnancement'
      addNotification(errorMessage, 'error')
    } finally {
      setIsReorderingBlocks(false)
    }
  }, [reorderLessonBlocks, saveToHistory, addNotification])

  // Contextual tips
  useEffect(() => {
    if (selectedBlock) {
      if (selectedBlock.type === 'QUIZ' && (!selectedBlock.content?.questions || selectedBlock.content.questions.length === 0)) {
        addTip('Le quiz doit avoir au moins une question')
      }
    }
  }, [selectedBlock, addTip])

  // Loading state (only if not creating new)
  if (!isCreatingNew && (courseLoading || modulesLoading)) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
            <p className="text-gray-600">Chargement de l'éditeur...</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state (only if not creating new)
  if (!isCreatingNew && (courseError || !course)) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="flex-1 p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Erreur lors du chargement du cours</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="flex-1 flex flex-col overflow-hidden w-full">
        {/* Header */}
        <EditorHeader
          course={currentCourse}
          onSave={handleSave}
          onPreview={handlePreview}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          isSaving={isSaving}
          saveStatus={saveStatus}
          onQuit={() => navigate('/admin/courses')}
          onDeleteCourse={handleCourseDelete}
          onStatusChange={handleCourseStatusChange}
          onTogglePublished={handleTogglePublished}
          isPublishing={isPublishing}
          isCreatingNew={isCreatingNew}
          onShowTranslations={async (languageCode) => {
            const selectedEntity = getSelectedEntityFromHierarchy()
            if (selectedEntity) {
              // Si c'est un quiz et qu'on change de langue, recharger le quiz avec la traduction
              if (selectedEntity.type === 'quiz' && selectedEntity.id && languageCode) {
                const quizWithTranslation = await loadQuizWithTranslation(selectedEntity.id, languageCode)
                if (quizWithTranslation) {
                  setEditingQuiz(quizWithTranslation)
                }
              }
              
              setTranslationType(selectedEntity.type)
              setTranslationEntityId(selectedEntity.id)
              setTranslationEntityData(selectedEntity.data)
              setTranslationInitialLanguage(languageCode || null)
              setShowTranslations(true)
              setCanvasView(null)
            } else {
              addNotification('Veuillez sélectionner un élément dans la hiérarchie pour gérer ses traductions', 'info')
            }
          }}
          showInspector={showInspector}
          onToggleInspector={() => setShowInspector(prev => !prev)}
        />

        {/* Main Content Area */}
        {isCreatingNew && !courseCreated ? (
          // Show course creation form in center when creating new course
          <CourseCreationForm
            onSave={handleCreateCourseFromModal}
            onCancel={() => navigate('/admin/courses')}
          />
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Sidebar */}
            <EditorSidebar
            modules={modules}
            selectedModuleId={selectedModuleId}
            selectedLessonId={selectedLessonId}
            selectedBlockId={selectedBlockId}
            selectedQuizId={selectedQuizId}
            onModuleSelect={handleModuleSelect}
            onLessonSelect={handleLessonSelect}
            onBlockSelect={handleBlockSelect}
            onQuizSelect={handleQuizSelect}
            onCreateModule={() => setCanvasView('create-module')}
            onCreateLesson={(moduleId) => {
              setSelectedModuleId(moduleId)
              setCanvasView('create-lesson')
            }}
            onCreateBlock={(type, lessonId) => {
              setCreatingBlockType(type)
              if (lessonId) setSelectedLessonId(lessonId)
              setCanvasView('create-block')
            }}
            onCreateQuiz={(moduleId) => {
              if (moduleId) {
                setSelectedModuleId(moduleId)
              }
              setCanvasView('create-quiz')
            }}
            onModuleReorder={handleModuleReorder}
            onBlockReorder={handleBlockReorder}
            onModuleMoveUp={handleModuleMoveUp}
            onModuleMoveDown={handleModuleMoveDown}
            onModuleDelete={handleModuleDelete}
            onLessonDelete={handleLessonDelete}
            onBlockDelete={handleBlockDelete}
            onQuizDelete={handleQuizDelete}
            isCreatingModule={isCreatingModule}
            isReorderingBlocks={isReorderingBlocks}
            course={currentCourse}
            onCourseSelect={handleCourseSelect}
            selectedCourseId={selectedCourseId || (currentCourse?.id && !selectedModuleId && !selectedLessonId && !selectedBlockId && !selectedQuizId ? currentCourse.id : null)}
          />

          {/* Central Canvas */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden h-full">
          {/* Formulaires de création/édition - priorité la plus haute */}
          {canvasView === 'create-module' && (
            <ModuleForm
              onSave={handleCreateModule}
              onCancel={() => {
                setCanvasView(null)
                setEditingModule(null)
              }}
              modulesCount={modules.length}
            />
          )}
          {canvasView === 'edit-module' && editingModule && (
            <ModuleForm
              module={editingModule}
              onSave={handleUpdateModule}
              onCancel={() => {
                setCanvasView(null)
                setEditingModule(null)
              }}
              modulesCount={modules.length}
              onShowTranslations={() => {
                const selectedEntity = getSelectedEntityFromHierarchy()
                if (selectedEntity) {
                  setTranslationType(selectedEntity.type)
                  setTranslationEntityId(selectedEntity.id)
                  setTranslationEntityData(selectedEntity.data)
                  setShowTranslations(true)
                  setCanvasView(null)
                } else {
                  addNotification('Veuillez sélectionner un élément dans la hiérarchie pour gérer ses traductions', 'info')
                }
              }}
            />
          )}
          {canvasView === 'create-lesson' && (
            <LessonForm
              lesson={null}
              onSave={(lessonData) => handleCreateLesson(lessonData, selectedModuleId)}
              onCancel={() => {
                setCanvasView(null)
                setEditingLesson(null)
              }}
              lessonsCount={currentLessons?.length || 0}
            />
          )}
          {canvasView === 'edit-lesson' && editingLesson && (
            <LessonForm
              lesson={editingLesson}
              onSave={handleUpdateLesson}
              onCancel={() => {
                setCanvasView(null)
                setEditingLesson(null)
              }}
              lessonsCount={currentLessons?.length || 0}
              onShowTranslations={() => {
                const selectedEntity = getSelectedEntityFromHierarchy()
                if (selectedEntity) {
                  setTranslationType(selectedEntity.type)
                  setTranslationEntityId(selectedEntity.id)
                  setTranslationEntityData(selectedEntity.data)
                  setShowTranslations(true)
                  setCanvasView(null)
                } else {
                  addNotification('Veuillez sélectionner un élément dans la hiérarchie pour gérer ses traductions', 'info')
                }
              }}
            />
          )}
          {canvasView === 'create-block' && (
            <BlockForm
              blockType={creatingBlockType}
              onSave={(blockData) => handleCreateBlock(blockData, selectedLessonId)}
              onCancel={() => {
                setCanvasView(null)
                setCreatingBlockType(null)
              }}
              blocksCount={currentBlocks?.length || 0}
              existingBlocks={currentBlocks || []}
            />
          )}
          {canvasView === 'edit-block' && editingBlock && (
            <BlockForm
              block={editingBlock}
              onSave={handleUpdateBlock}
              onCancel={() => {
                setCanvasView(null)
                setEditingBlock(null)
              }}
              blocksCount={currentBlocks?.length || 0}
              onShowTranslations={() => {
                const selectedEntity = getSelectedEntityFromHierarchy()
                if (selectedEntity) {
                  setTranslationType(selectedEntity.type)
                  setTranslationEntityId(selectedEntity.id)
                  setTranslationEntityData(selectedEntity.data)
                  setShowTranslations(true)
                  setCanvasView(null)
                } else {
                  addNotification('Veuillez sélectionner un élément dans la hiérarchie pour gérer ses traductions', 'info')
                }
              }}
            />
          )}
          {canvasView === 'create-quiz' && (
            <QuizForm
              quiz={null}
              onSave={async (quizData) => {
                console.log('QuizForm onSave appelé avec quizData:', quizData)
                const result = await handleCreateQuiz(quizData, selectedModuleId)
                console.log('handleCreateQuiz a retourné:', result)
                return result
              }}
              onCancel={() => {
                setCanvasView(null)
                setEditingQuiz(null)
              }}
            />
          )}
          {canvasView === 'edit-quiz' && editingQuiz && (
            isLoadingQuiz ? (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
                  <p className="text-gray-600">Chargement du quiz...</p>
                </div>
              </div>
            ) : (
              <QuizForm
                quiz={editingQuiz}
                onSave={handleUpdateQuiz}
                onCancel={() => {
                  setCanvasView(null)
                  setEditingQuiz(null)
                }}
                onQuestionsCreated={() => {
                  // Recharger les modules après la création des questions
                  refetchModules()
                }}
                onShowTranslations={() => {
                  const selectedEntity = getSelectedEntityFromHierarchy()
                  if (selectedEntity) {
                    setTranslationType(selectedEntity.type)
                    setTranslationEntityId(selectedEntity.id)
                    setTranslationEntityData(selectedEntity.data)
                    setShowTranslations(true)
                    setCanvasView(null)
                  } else {
                    addNotification('Veuillez sélectionner un élément dans la hiérarchie pour gérer ses traductions', 'info')
                  }
                }}
              />
            )
          )}
          {canvasView === 'edit-course-image' && currentCourse && (
            <CourseImageForm
              course={currentCourse}
              onSave={async (data) => {
                try {
                  await updateCourse(courseId, data)
                  refetchCourse()
                  saveToHistory()
                  addNotification('Image du cours mise à jour', 'success')
                  setCanvasView(null)
                } catch (err) {
                  logger.error('Error updating course image', err)
                  addNotification('Erreur lors de la mise à jour de l\'image', 'error')
                }
              }}
              onCancel={() => setCanvasView(null)}
            />
          )}
          {canvasView === 'edit-course-configuration' && (
            isLoadingCourseConfig ? (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
                  <p className="text-gray-600">Chargement de la configuration...</p>
                </div>
              </div>
            ) : courseConfigData ? (
              <CourseConfigurationForm
                course={courseConfigData}
                onSave={async (data) => {
                  try {
                    await updateCourse(courseId, data)
                    refetchCourse()
                    // Mettre à jour courseConfigData avec les nouvelles données
                    setCourseConfigData(null)
                    saveToHistory()
                    addNotification('Configuration du cours mise à jour', 'success')
                    setCanvasView(null)
                  } catch (err) {
                    logger.error('Error updating course configuration', err)
                    addNotification('Erreur lors de la mise à jour de la configuration', 'error')
                  }
                }}
                onCancel={() => {
                  setCourseConfigData(null)
                  setCanvasView(null)
                }}
              />
            ) : null
          )}
          {canvasView === 'edit-course' && (
            <CourseEditForm
              course={isCreatingNew ? { ...newCourseData, title: newCourseData.title || 'Nouveau cours' } : currentCourse}
              onSave={async (data) => {
                try {
                  if (isCreatingNew) {
                    // If creating new, update the newCourseData state
                    setNewCourseData(prev => ({ ...prev, ...data }))
                    addNotification('Informations du cours mises à jour', 'success')
                  } else {
                    // If editing existing, update via API
                    await updateCourse(courseId, data)
                    refetchCourse()
                    saveToHistory()
                    addNotification('Informations du cours mises à jour', 'success')
                  }
                  setCanvasView(null)
                } catch (err) {
                  logger.error('Error updating course info', err)
                  addNotification('Erreur lors de la mise à jour', 'error')
                }
              }}
              onCancel={() => setCanvasView(null)}
              isCreatingNew={isCreatingNew}
              onShowTranslations={() => {
                const selectedEntity = getSelectedEntityFromHierarchy()
                if (selectedEntity) {
                  setTranslationType(selectedEntity.type)
                  setTranslationEntityId(selectedEntity.id)
                  setTranslationEntityData(selectedEntity.data)
                  setShowTranslations(true)
                  setCanvasView(null)
                } else {
                  addNotification('Veuillez sélectionner un élément dans la hiérarchie pour gérer ses traductions', 'info')
                }
              }}
            />
          )}

          {/* Panneau de traduction */}
          {showTranslations && translationType && translationEntityId && !canvasView && (
            <TranslationManager
              type={translationType}
              entityId={translationEntityId}
              entityData={translationEntityData}
              initialLanguage={translationInitialLanguage}
              onClose={() => {
                setShowTranslations(false)
                setTranslationType(null)
                setTranslationEntityId(null)
                setTranslationEntityData(null)
                setTranslationInitialLanguage(null)
              }}
              onLanguageChange={async (type, entityId, languageCode) => {
                // Recharger l'entité traduite dans le contenu central
                if (type === 'quiz' && entityId) {
                  const quizWithTranslation = await loadQuizWithTranslation(entityId, languageCode)
                  if (quizWithTranslation) {
                    setEditingQuiz(quizWithTranslation)
                    // Mettre à jour aussi les données du panneau de traduction
                    setTranslationEntityData(quizWithTranslation)
                  }
                }
              }}
              onTranslationSaved={async () => {
                // Rafraîchir les données si nécessaire
                if (translationType === 'course') {
                  refetchCourse()
                } else if (translationType === 'module') {
                  refetchModules()
                } else if (translationType === 'lesson') {
                  refetchModules()
                } else if (translationType === 'block') {
                  refetchModules()
                } else if (translationType === 'quiz') {
                  // Pour les quiz, recharger le quiz pour mettre à jour l'inspecteur
                  if (translationEntityId) {
                    // Recharger le quiz avec la traduction actuelle
                    const quizWithTranslation = await loadQuizWithTranslation(translationEntityId, translationInitialLanguage || 'fr')
                    if (quizWithTranslation) {
                      setEditingQuiz(quizWithTranslation)
                    }
                    // Forcer le rechargement du quiz en réinitialisant selectedQuizId
                    const currentQuizId = selectedQuizId
                    setSelectedQuizId(null)
                    setTimeout(() => {
                      setSelectedQuizId(currentQuizId)
                    }, 100)
                  }
                  refetchModules()
                }
              }}
            />
          )}

          {/* Previews des éléments sélectionnés - affichés seulement si aucun formulaire ni traduction n'est ouvert */}
          {!canvasView && !showTranslations && selectedQuizId && (
            isLoadingQuiz || !editingQuiz ? (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
                  <p className="text-gray-600">Chargement du quiz...</p>
                </div>
              </div>
            ) : (
              <QuizPreview
                quiz={editingQuiz}
                onEdit={() => {
                  setCanvasView('edit-quiz')
                }}
              />
            )
          )}
          {!canvasView && !showTranslations && !selectedQuizId && selectedBlockId && (
            isLoadingBlock || !editingBlock ? (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
                  <p className="text-gray-600">Chargement du bloc...</p>
                </div>
              </div>
            ) : (
              <BlockPreview
                block={editingBlock}
                onEdit={() => {
                  const block = currentBlocks?.find(b => b.id === selectedBlockId) || editingBlock
                  if (block) {
                    setEditingBlock(block)
                    setCanvasView('edit-block')
                  }
                }}
              />
            )
          )}
          {!canvasView && !showTranslations && !selectedQuizId && !selectedBlockId && selectedLessonId && (
            isLoadingLesson || !editingLesson ? (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#141835] mx-auto mb-4" />
                  <p className="text-gray-600">Chargement du chapitre...</p>
                </div>
              </div>
            ) : (
              <LessonPreview
                lesson={editingLesson}
                onEdit={() => {
                  const lesson = currentLessons?.find(l => l.id === selectedLessonId) || editingLesson
                  if (lesson) {
                    setEditingLesson(lesson)
                    setCanvasView('edit-lesson')
                  }
                }}
              />
            )
          )}
          {!canvasView && !showTranslations && !selectedQuizId && !selectedBlockId && !selectedLessonId && selectedModuleId && (
            <ModulePreview
              module={modules.find(m => m.id === selectedModuleId)}
              onEdit={() => {
                const module = modules.find(m => m.id === selectedModuleId)
                if (module) {
                  setEditingModule(module)
                  setCanvasView('edit-module')
                }
              }}
            />
          )}
          {!canvasView && !showTranslations && !isCreatingNew && currentCourse && !selectedModuleId && !selectedLessonId && !selectedBlockId && !selectedQuizId && (
            <CoursePreviewContent
              course={currentCourse}
              modules={modules}
            />
          )}

          {/* Canvas par défaut - affiché seulement si rien d'autre n'est sélectionné */}
          {!canvasView && !showTranslations && !selectedQuizId && !selectedBlockId && !selectedModuleId && (!currentCourse || isCreatingNew) && (
            <EditorCanvas
              selectedModuleId={selectedModuleId}
              selectedBlockId={selectedBlockId}
              selectedBlock={selectedBlock}
              onBlockUpdate={async (blockId, data) => {
                // LOG: Données reçues dans onBlockUpdate (EditorCanvas)
                console.log('[AdvancedCourseEditorPage] onBlockUpdate (EditorCanvas) - Données reçues:', {
                  blockId,
                  data: {
                    ...data,
                    image: data.image ? {
                      name: data.image.name,
                      type: data.image.type,
                      size: data.image.size,
                      isFile: data.image instanceof File
                    } : undefined
                  }
                })
                
                try {
                  setIsLoadingBlock(true)
                  const result = await updateBlock(blockId, data)
                  
                  // LOG: Réponse reçue dans onBlockUpdate
                  console.log('[AdvancedCourseEditorPage] onBlockUpdate (EditorCanvas) - Réponse API:', result)
                  
                  // Rafraîchir les modules pour obtenir les données à jour
                  await refetchModules()
                  
                  // Recharger le bloc mis à jour depuis l'API
                  try {
                    const response = await api.get(endpoints.getModuleBlock(blockId))
                    const updatedBlockData = response?.data || response
                    if (updatedBlockData) {
                      setEditingBlock(updatedBlockData)
                    }
                  } catch (err) {
                    logger.error('Error reloading block after update', err)
                    // Fallback: utiliser les données de la réponse de mise à jour si disponibles
                    if (result) {
                      setEditingBlock(result)
                    }
                  } finally {
                    setIsLoadingBlock(false)
                  }
                  
                  saveToHistory()
                  addNotification('Bloc mis à jour', 'success')
                } catch (err) {
                  // LOG: Erreur dans onBlockUpdate
                  console.error('[AdvancedCourseEditorPage] onBlockUpdate (EditorCanvas) - Erreur:', err)
                  setIsLoadingBlock(false) // S'assurer que le loading est désactivé en cas d'erreur
                  logger.error('Error updating block', err)
                  addNotification('Erreur lors de la mise à jour', 'error')
                }
              }}
              onBlockDelete={async (blockId) => {
                try {
                  await deleteBlock(blockId)
                  setSelectedBlockId(null)
                  refetchModules()
                  saveToHistory()
                  addNotification('Bloc supprimé', 'success')
                } catch (err) {
                  logger.error('Error deleting block', err)
                  addNotification('Erreur lors de la suppression', 'error')
                }
              }}
              blocksLoading={blocksLoading}
              onCreateBlock={(type) => {
                setCreatingBlockType(type)
                setCanvasView('create-block')
              }}
              isCreatingNew={isCreatingNew}
            />
          )}
          </div>

          {/* Right Sidebar - Inspector Panel */}
          {showInspector && (
            <InspectorPanel
              selectedCourse={!isCreatingNew && currentCourse && !selectedModuleId && !selectedLessonId && !selectedBlockId && !selectedQuizId ? currentCourse : null}
              selectedModule={selectedModuleId && !selectedLessonId && !selectedBlockId && !selectedQuizId ? (editingModule || modules.find(m => m.id === selectedModuleId)) : null}
              selectedLesson={selectedLessonId && !selectedBlockId && !selectedQuizId && !isLoadingLesson ? (editingLesson || (() => {
                // Chercher la leçon dans currentLessons d'abord
                if (currentLessons?.length > 0) {
                  const lesson = currentLessons.find(l => l.id === selectedLessonId)
                  if (lesson) return lesson
                }
                // Sinon, chercher dans tous les modules
                for (const module of modules) {
                  if (module.lessons && Array.isArray(module.lessons)) {
                    const lesson = module.lessons.find(l => l.id === selectedLessonId)
                    if (lesson) return lesson
                  }
                }
                return null
              })()) : null}
              isLoadingLesson={isLoadingLesson && selectedLessonId ? true : false}
              selectedBlock={selectedBlockId && !selectedQuizId && !isLoadingBlock ? (editingBlock || (() => {
                // Chercher le bloc dans currentBlocks d'abord
                if (currentBlocks?.length > 0) {
                  const block = currentBlocks.find(b => b.id === selectedBlockId)
                  if (block) return block
                }
                // Sinon, chercher dans toutes les leçons de tous les modules
                for (const module of modules) {
                  if (module.lessons && Array.isArray(module.lessons)) {
                    for (const lesson of module.lessons) {
                      if (lesson.blocks && Array.isArray(lesson.blocks)) {
                        const block = lesson.blocks.find(b => b.id === selectedBlockId)
                        if (block) return block
                      }
                    }
                  }
                }
                return null
              })()) : null}
              isLoadingBlock={isLoadingBlock && selectedBlockId ? true : false}
              selectedQuiz={selectedQuizId && !isLoadingQuiz && editingQuiz ? editingQuiz : null}
              isLoadingQuiz={isLoadingQuiz && selectedQuizId ? true : false}
              onClose={() => setShowInspector(false)}
              onEdit={async () => {
                if (!isCreatingNew && currentCourse && !selectedModuleId && !selectedLessonId && !selectedBlockId && !selectedQuizId) {
                  setCanvasView('edit-course')
                } else if (selectedQuizId) {
                  // Charger le quiz complet avant d'ouvrir le formulaire
                  setIsLoadingQuiz(true)
                  try {
                    const response = await api.get(endpoints.getQuizAdmin(selectedQuizId))
                    const quizData = response?.data || response
                    if (quizData) {
                      setEditingQuiz(quizData)
                      setCanvasView('edit-quiz')
                    } else {
                      // Fallback: chercher dans les modules
                      let quiz = null
                      for (const module of modules) {
                        if (module.quiz) {
                          const moduleQuiz = module.quiz.quiz || module.quiz
                          if (moduleQuiz.id === selectedQuizId) {
                            quiz = moduleQuiz
                            break
                          }
                        }
                      }
                      if (quiz) {
                        setEditingQuiz(quiz)
                        setCanvasView('edit-quiz')
                      }
                    }
                  } catch (err) {
                    logger.error('Error loading quiz for edit', err)
                    // Fallback: chercher dans les modules
                    let quiz = null
                    for (const module of modules) {
                      if (module.quiz) {
                        const moduleQuiz = module.quiz.quiz || module.quiz
                        if (moduleQuiz.id === selectedQuizId) {
                          quiz = moduleQuiz
                          break
                        }
                      }
                    }
                    if (quiz) {
                      setEditingQuiz(quiz)
                      setCanvasView('edit-quiz')
                    }
                  } finally {
                    setIsLoadingQuiz(false)
                  }
                } else if (selectedLessonId) {
                  const lesson = currentLessons?.find(l => l.id === selectedLessonId)
                  if (lesson) {
                    setEditingLesson(lesson)
                    setCanvasView('edit-lesson')
                  }
                } else if (selectedModuleId) {
                  const module = modules.find(m => m.id === selectedModuleId)
                  if (module) {
                    setEditingModule(module)
                    setCanvasView('edit-module')
                  }
                } else if (selectedBlockId) {
                  const block = currentBlocks?.find(b => b.id === selectedBlockId)
                  if (block) {
                    setEditingBlock(block)
                    setCanvasView('edit-block')
                  }
                }
              }}
              onDelete={() => {
                if (selectedQuizId) {
                  handleQuizDelete(selectedQuizId)
                } else if (selectedBlockId) {
                  handleBlockDelete(selectedBlockId)
                } else if (selectedLessonId) {
                  handleLessonDelete(selectedLessonId)
                } else if (selectedModuleId) {
                  handleModuleDelete(selectedModuleId)
                }
              }}
              onAddModule={() => {
                setCanvasView('create-module')
              }}
              onAddLesson={() => {
                if (selectedModuleId) {
                  setCanvasView('create-lesson')
                }
              }}
              onAddBlock={(type) => {
                setCreatingBlockType(type || 'TEXT')
                setCanvasView('create-block')
              }}
              onAddQuiz={() => {
                setCanvasView('create-quiz')
              }}
              onLanguageChange={async (type, entityId, languageCode) => {
                // Pour les quiz, recharger le quiz traduit et l'afficher dans le contenu central
                if (type === 'quiz' && entityId) {
                  const quizWithTranslation = await loadQuizWithTranslation(entityId, languageCode)
                  if (quizWithTranslation) {
                    setEditingQuiz(quizWithTranslation)
                  }
                }
              }}
              onShowTranslations={async (languageCode) => {
                const selectedEntity = getSelectedEntityFromHierarchy()
                if (selectedEntity) {
                  // Pour les quiz, charger le quiz de base (en français) comme données de référence
                  let entityDataToUse = selectedEntity.data
                  
                  if (selectedEntity.type === 'quiz' && selectedEntity.id) {
                    // Charger le quiz de base en français pour avoir toutes les données (questions, etc.)
                    const baseQuiz = await loadQuizWithTranslation(selectedEntity.id, 'fr')
                    if (baseQuiz) {
                      entityDataToUse = baseQuiz
                    }
                  }
                  
                  // Ouvrir le panneau de traduction avec la langue demandée
                  // Le panneau chargera automatiquement la traduction via handleLanguageChange
                  setTranslationType(selectedEntity.type)
                  setTranslationEntityId(selectedEntity.id)
                  setTranslationEntityData(entityDataToUse)
                  setTranslationInitialLanguage(languageCode || null)
                  setShowTranslations(true)
                  setCanvasView(null)
                } else {
                  addNotification('Veuillez sélectionner un élément dans la hiérarchie pour gérer ses traductions', 'info')
                }
              }}
              onEditImage={() => {
                if (!isCreatingNew && currentCourse) {
                  setCanvasView('edit-course-image')
                }
              }}
              onEditConfiguration={async () => {
                if (!isCreatingNew && courseId) {
                  setIsLoadingCourseConfig(true)
                  setCourseConfigData(null)
                  try {
                    // Charger les données complètes du cours depuis l'API
                    const response = await api.get(endpoints.getCourse(courseId))
                    const courseData = response?.data || response
                    if (courseData) {
                      setCourseConfigData(courseData)
                      setCanvasView('edit-course-configuration')
                    } else {
                      addNotification('Erreur lors du chargement des données du cours', 'error')
                    }
                  } catch (err) {
                    logger.error('Error loading course for configuration', err)
                    addNotification('Erreur lors du chargement des données du cours', 'error')
                  } finally {
                    setIsLoadingCourseConfig(false)
                  }
                }
              }}
            />
          )}
          </div>
        )}

        {/* Footer */}
        <EditorFooter
          saveStatus={saveStatus}
          lastSaved={lastSaved}
          tips={tips}
          notifications={notifications}
          onRemoveTip={removeTip}
          onRemoveNotification={(id) => {
            setNotifications(prev => prev.filter(n => n.id !== id))
          }}
        />

        {/* Course Preview Modal */}
        {showPreviewModal && (
          <CoursePreviewModal
            course={currentCourse}
            modules={modules}
            onClose={() => setShowPreviewModal(false)}
          />
        )}
      </div>
    </div>
  )
}

